import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, fileToBase64 } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../components/ui/command";
import { Plus, Trash2, Sparkles, Loader2, Save, Eye, ArrowLeft, Upload, Image as ImageIcon, X, Package, Wrench, Users, Check, Gift, Search, FolderPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";

const emptyItem = () => ({
  id: crypto.randomUUID(),
  title: "",
  quantity: 1,
  price: 0,
  description: "",
  image: "",
  gallery: [],
  condition: "nuovo",
  discount: 0,
  gifts: [],
  specs: {},
  product_type: "standard",
  spec_rows: [],
  warranty_months: 0,
  acquisition: "",
  rental_months: 0,
});

const GIFT_OPTIONS = [
  "Borsa Notebook protettiva",
  "Panno copri tastiera, in microfibra proteggi Monitor",
  "Kit Tastiera + Mouse Logitech Wireless",
];

const SPEC_FIELDS = [
  ["processore", "Processore"],
  ["ram", "RAM"],
  ["hdd", "HDD"],
  ["monitor", "Monitor"],
  ["scheda_video", "Scheda Video"],
  ["sistema_operativo", "Sist. Operativo"],
];

const SERVER_SPEC_LABELS = [
  "Processor - CPU",
  "Memory - RAM",
  "Main Storage Adapter - SAS SATA NVMe Controller",
  "Hard Disk Drive - HDD",
  "Hard Drive Caddy - Blank Filler",
  "Server-Expansions - Management and Controllers",
  "Controller Extension - PCIe",
  "Software Pre-Installation",
  "Power Supply - PSU",
  "Server Rack Rail Kit",
  "Warranty Extension",
];

const fmt = (n, c = "EUR") =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: c }).format(n || 0);

export default function QuoteBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [form, setForm] = useState({
    title: "",
    client_name: "",
    client_email: "",
    signing_email: "",
    client_address: "",
    client_logo: "",
    notes: "",
    currency: "EUR",
    tax_rate: 22,
    status: "draft",
    supply_type: "",
    offer_type: "acquisto",
    rental_months: 36,
    monthly_fee: 0,
    cover_enabled: true,
    cover_title: "",
    cover_subtitle: "",
    cover_text: "",
    closing_enabled: true,
    closing_title: "",
    valid_until: "",
    payment_terms: "",
    vat_note: "IVA esclusa",
    signing_place: "",
    closing_text: "",
    items: [emptyItem()],
  });
  const [coverLoading, setCoverLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [customGift, setCustomGift] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [clientLogos, setClientLogos] = useState([]);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [savingLogo, setSavingLogo] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [aiLoading, setAiLoading] = useState({}); // itemId -> bool
  const [imgLoading, setImgLoading] = useState({}); // itemId -> bool
  const [varLoading, setVarLoading] = useState({}); // itemId -> bool
  const [varCount, setVarCount] = useState({}); // itemId -> number
  useEffect(() => {
    if (editing) {
      api.get(`/quotes/${id}`)
        .then((r) => setForm({
          ...r.data,
          supply_type: r.data.supply_type ?? "",
          cover_enabled: r.data.cover_enabled ?? false,
          cover_title: r.data.cover_title ?? "",
          cover_subtitle: r.data.cover_subtitle ?? "",
          cover_text: r.data.cover_text ?? "",
          closing_enabled: r.data.closing_enabled ?? false,
          closing_title: r.data.closing_title ?? "",
          valid_until: r.data.valid_until ?? "",
          payment_terms: r.data.payment_terms ?? "",
          vat_note: r.data.vat_note ?? "IVA esclusa",
          signing_place: r.data.signing_place ?? "",
          closing_text: r.data.closing_text ?? "",
          items: r.data.items?.length ? r.data.items : [emptyItem()],
        }))
        .catch(() => toast.error("Preventivo non trovato"))
        .finally(() => setLoading(false));
    }
  }, [id, editing]);

  useEffect(() => {
    const raw = sessionStorage.getItem("customers_cache");
    if (raw) {
      try {
        const { ts, list } = JSON.parse(raw);
        if (Array.isArray(list)) setCustomers(list);
        if (ts && Date.now() - ts < 300000) return; // fresh (5 min) → no API call
      } catch (_) {}
    }
    api.get("/customers").then((r) => {
      const list = r.data.customers || [];
      setCustomers(list);
      try { sessionStorage.setItem("customers_cache", JSON.stringify({ ts: Date.now(), list })); } catch (_) {}
    }).catch(() => {});
  }, []);

  const loadClientLogos = useCallback(() => {
    api.get("/client-logos").then((r) => setClientLogos(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadClientLogos(); }, [loadClientLogos]);

  const applyLogo = (logo) => {
    update("client_logo", logo.image);
    setLogoDialogOpen(false);
    toast.success("Logo cliente applicato");
  };

  const saveCurrentLogo = async () => {
    if (!form.client_logo) { toast.error("Carica prima un logo"); return; }
    const label = (form.client_name || "").trim() || "Logo cliente";
    const exists = clientLogos.find(
      (l) => (l.client_name || "").toLowerCase() === (form.client_name || "").toLowerCase() && form.client_name
    );
    if (exists && exists.image === form.client_logo) { toast.info("Logo già salvato per questo cliente"); return; }
    setSavingLogo(true);
    try {
      await api.post("/client-logos", { label, client_name: (form.client_name || "").trim(), image: form.client_logo });
      loadClientLogos();
      toast.success("Logo salvato in libreria");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingLogo(false); }
  };

  const deleteLogo = async (lid) => {
    try {
      await api.delete(`/client-logos/${lid}`);
      setClientLogos((prev) => prev.filter((l) => l.id !== lid));
      toast.success("Logo eliminato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadTemplate = async (type) => {
    update("supply_type", type);
    try {
      const { data } = await api.get(`/templates/${type}`);
      setForm((f) => ({
        ...f,
        supply_type: type,
        cover_enabled: true,
        closing_enabled: true,
        cover_subtitle: data.cover_subtitle ?? f.cover_subtitle,
        cover_text: data.cover_text ?? f.cover_text,
        closing_title: data.closing_title ?? f.closing_title,
        closing_text: data.closing_text ?? f.closing_text,
      }));
      toast.success(`Template "${type === "materiale" ? "Materiale" : "Servizi"}" caricato`);
    } catch (err) {
      toast.error("Errore caricamento template");
    }
  };

  const selectCustomer = (cust) => {
    const saved = clientLogos.find(
      (l) => (l.client_name || "").toLowerCase().trim() === (cust.name || "").toLowerCase().trim() && l.client_name
    );
    setForm((f) => ({
      ...f,
      client_name: cust.name,
      client_email: cust.email || f.client_email,
      signing_email: cust.email || f.signing_email,
      client_address: cust.phone ? `Tel. ${cust.phone}` : f.client_address,
      client_logo: saved ? saved.image : f.client_logo,
    }));
    setCustomerOpen(false);
    toast.success(saved ? "Cliente e logo caricati dall'anagrafica" : "Cliente caricato dall'anagrafica");
  };

  const generateCover = async () => {
    if (!form.title.trim()) {
      toast.error("Inserisci prima il titolo del preventivo");
      return;
    }
    setCoverLoading(true);
    try {
      const { data } = await api.post("/ai/generate-cover", {
        title: form.title,
        items: form.items.map((i) => i.title).filter(Boolean),
      });
      setForm((f) => ({
        ...f,
        cover_title: data.cover_title || f.cover_title,
        cover_subtitle: data.cover_subtitle || f.cover_subtitle,
        cover_text: data.cover_text || f.cover_text,
        closing_text: data.closing_text || f.closing_text,
      }));
      toast.success("Copertina generata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore copertina AI");
    } finally {
      setCoverLoading(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateItem = (itemId, key, value) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => (it.id === itemId ? { ...it, [key]: value } : it)),
    }));

  const addCustomGift = (item) => {
    const v = (customGift[item.id] || "").trim();
    if (!v) return;
    const cur = item.gifts || [];
    if (!cur.includes(v)) updateItem(item.id, "gifts", [...cur, v]);
    setCustomGift((m) => ({ ...m, [item.id]: "" }));
  };

  const updateSpec = (itemId, key, value) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it) =>
        it.id === itemId ? { ...it, specs: { ...(it.specs || {}), [key]: value } } : it),
    }));

  const setProductType = (item, type) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => {
        if (it.id !== item.id) return it;
        let rows = it.spec_rows || [];
        if (type === "server" && rows.length === 0) {
          rows = SERVER_SPEC_LABELS.map((label) => ({ label, value: "" }));
        }
        return { ...it, product_type: type, spec_rows: rows };
      }),
    }));

  const updateSpecRow = (itemId, index, key, value) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => {
        if (it.id !== itemId) return it;
        const rows = [...(it.spec_rows || [])];
        rows[index] = { ...rows[index], [key]: value };
        return { ...it, spec_rows: rows };
      }),
    }));

  const addSpecRow = (itemId) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it) =>
        it.id === itemId ? { ...it, spec_rows: [...(it.spec_rows || []), { label: "", value: "" }] } : it),
    }));

  const removeSpecRow = (itemId, index) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it) =>
        it.id === itemId ? { ...it, spec_rows: (it.spec_rows || []).filter((_, i) => i !== index) } : it),
    }));

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));

  useEffect(() => { api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const addFromCatalog = (p) => {
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        id: crypto.randomUUID(), title: p.title, quantity: p.quantity || 1, price: p.price || 0,
        description: p.description || "", image: p.image || "", gallery: p.gallery || [], condition: p.condition || "nuovo",
        discount: p.discount || 0, gifts: p.gifts || [], specs: p.specs || {},
      }],
    }));
    toast.success(`"${p.title}" aggiunto all'offerta`);
    setCatalogOpen(false);
  };

  const saveItemToCatalog = async (item) => {
    if (!item.title.trim()) { toast.error("La voce deve avere un titolo"); return; }
    try {
      const { data } = await api.post("/catalog", {
        title: item.title, price: Number(item.price) || 0, quantity: Number(item.quantity) || 1,
        description: item.description || "", image: item.image || "", gallery: item.gallery || [], condition: item.condition || "nuovo",
        discount: Number(item.discount) || 0, gifts: item.gifts || [], specs: item.specs || {},
      });
      setCatalog((c) => [...c, data]);
      toast.success("Voce salvata nel catalogo");
    } catch { toast.error("Errore salvataggio nel catalogo"); }
  };

  const removeItem = (itemId) =>
    setForm((f) => ({ ...f, items: f.items.filter((it) => it.id !== itemId) }));

  const generateAI = useCallback(async (item) => {
    if (!item.title.trim()) {
      toast.error("Inserisci prima il titolo del prodotto");
      return;
    }
    setAiLoading((s) => ({ ...s, [item.id]: true }));
    try {
      const { data } = await api.post("/ai/generate-description", {
        title: item.title,
        price: Number(item.price) || null,
        currency: form.currency,
        condition: item.condition || "nuovo",
      });
      updateItem(item.id, "description", data.description);
      toast.success("Descrizione generata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore AI");
    } finally {
      setAiLoading((s) => ({ ...s, [item.id]: false }));
    }
  }, [form.currency]);

  const generateImage = useCallback(async (item) => {
    if (!item.title.trim()) {
      toast.error("Inserisci prima il titolo del prodotto");
      return;
    }
    setImgLoading((s) => ({ ...s, [item.id]: true }));
    try {
      const { data } = await api.post("/ai/generate-image", { title: item.title });
      updateItem(item.id, "image", data.image);
      toast.success("Immagine generata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore immagine AI");
    } finally {
      setImgLoading((s) => ({ ...s, [item.id]: false }));
    }
  }, []);

  const uploadImage = useCallback(async (item, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 5MB)");
      return;
    }
    setImgLoading((s) => ({ ...s, [item.id]: true }));
    try {
      const dataUrl = await fileToBase64(file);
      const { data } = await api.post("/images/process", { image: dataUrl });
      updateItem(item.id, "image", data.image);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore caricamento immagine");
    } finally {
      setImgLoading((s) => ({ ...s, [item.id]: false }));
    }
  }, []);

  const uploadGallery = useCallback(async (item, files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setImgLoading((s) => ({ ...s, [item.id]: true }));
    try {
      const processed = [];
      for (const file of list) {
        if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: troppo grande (max 5MB)`); continue; }
        const dataUrl = await fileToBase64(file);
        const { data } = await api.post("/images/process", { image: dataUrl });
        processed.push(data.image);
      }
      setForm((f) => ({
        ...f,
        items: f.items.map((it) => it.id === item.id ? { ...it, gallery: [...(it.gallery || []), ...processed] } : it),
      }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore caricamento immagini");
    } finally {
      setImgLoading((s) => ({ ...s, [item.id]: false }));
    }
  }, []);

  const generateVariations = useCallback(async (item) => {
    const refs = [item.image, ...(item.gallery || [])].filter(Boolean);
    if (!refs.length) { toast.error("Carica prima almeno una foto del prodotto"); return; }
    const count = varCount[item.id] || 3;
    setVarLoading((s) => ({ ...s, [item.id]: true }));
    try {
      const { data } = await api.post("/ai/generate-image-variations", {
        title: item.title, images: refs, count,
      });
      setForm((f) => ({
        ...f,
        items: f.items.map((it) => it.id === item.id ? { ...it, gallery: [...(it.gallery || []), ...(data.images || [])] } : it),
      }));
      toast.success(`${(data.images || []).length} varianti generate`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore varianti AI");
    } finally {
      setVarLoading((s) => ({ ...s, [item.id]: false }));
    }
  }, [varCount]);

  const subtotal = form.items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 1) * (1 - (Number(i.discount) || 0) / 100), 0);
  const tax = subtotal * ((Number(form.tax_rate) || 0) / 100);
  const total = subtotal + tax;

  const lineTot = (i) => (Number(i.price) || 0) * (Number(i.quantity) || 1) * (1 - (Number(i.discount) || 0) / 100);
  const itemAcq = (i) => i.acquisition || form.offer_type || "acquisto";
  const itemMonths = (i) => Number(i.rental_months) || Number(form.rental_months) || 0;
  const nolItems = form.items.filter((i) => itemAcq(i) === "noleggio");
  const acqItems = form.items.filter((i) => itemAcq(i) !== "noleggio");
  const nolFee = nolItems.reduce((s, i) => s + (itemMonths(i) ? lineTot(i) / itemMonths(i) : 0), 0);
  const acqTotal = acqItems.reduce((s, i) => s + lineTot(i), 0);

  const buildPayload = () => ({
    ...form,
    tax_rate: Number(form.tax_rate) || 0,
    rental_months: Number(form.rental_months) || 0,
    monthly_fee: Number(form.monthly_fee) || 0,
    items: form.items
      .filter((i) => i.title.trim())
      .map((i) => ({ ...i, price: Number(i.price) || 0, quantity: Number(i.quantity) || 1, discount: Number(i.discount) || 0, rental_months: Number(i.rental_months) || 0 })),
  });

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Inserisci il titolo del preventivo");
      return;
    }
    setSaving(true);
    const payload = buildPayload();
    try {
      let res;
      if (editing) res = await api.put(`/quotes/${id}`, payload);
      else res = await api.post("/quotes", payload);
      toast.success("Preventivo salvato");
      navigate(`/quotes/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore di salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const previewDocument = async () => {
    if (!form.title.trim()) {
      toast.error("Inserisci il titolo del preventivo per l'anteprima");
      return;
    }
    // Open the tab synchronously inside the click gesture to avoid popup blockers,
    // then point it to the generated PDF once ready.
    const w = window.open("", "_blank");
    setPreviewing(true);
    try {
      const pdf = await api.post(`/quotes/preview/pdf`, buildPayload(), { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([pdf.data], { type: "application/pdf" }));
      if (w) {
        w.location = url;
      } else {
        // popup blocked: fall back to download
        const a = document.createElement("a");
        a.href = url; a.download = "anteprima.pdf";
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (w) w.close();
      toast.error(formatApiError(err.response?.data?.detail) || "Errore durante l'anteprima");
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) return <Layout><p className="text-slate-500">Caricamento…</p></Layout>;

  return (
    <Layout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Indietro
      </button>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">
          {editing ? "Modifica Preventivo" : "Nuovo Preventivo"}
        </h1>
        <div className="flex items-center gap-2">
          <Button onClick={previewDocument} disabled={previewing || saving} variant="outline" data-testid="preview-document-button">
            {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
            Anteprima documento
          </Button>
          <Button onClick={save} disabled={saving || previewing} data-testid="save-quote-button" className="bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salva
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: details + items */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Tipo di fornitura</h2>
            <p className="text-sm text-slate-500 mb-4">Carica i template di copertina e condizioni in base al tipo di offerta.</p>
            <div className="flex flex-wrap gap-3">
              <Button type="button" variant={form.supply_type === "materiale" ? "default" : "outline"}
                data-testid="supply-materiale-btn"
                className={form.supply_type === "materiale" ? "bg-blue-600 hover:bg-blue-700" : ""}
                onClick={() => loadTemplate("materiale")}>
                <Package className="w-4 h-4 mr-2" /> Materiale
              </Button>
              <Button type="button" variant={form.supply_type === "servizi" ? "default" : "outline"}
                data-testid="supply-servizi-btn"
                className={form.supply_type === "servizi" ? "bg-blue-600 hover:bg-blue-700" : ""}
                onClick={() => loadTemplate("servizi")}>
                <Wrench className="w-4 h-4 mr-2" /> Servizi
              </Button>
              {form.supply_type && (
                <span className="self-center text-xs text-slate-400">
                  Template caricato: puoi comunque modificare i testi qui sotto.
                </span>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-900">Pagina copertina</h2>
                <p className="text-sm text-slate-500">Pagina introduttiva davanti all'offerta, precaricata in base all'argomento.</p>
              </div>
              <Switch checked={form.cover_enabled} onCheckedChange={(v) => update("cover_enabled", v)}
                data-testid="cover-enabled-switch" />
            </div>
            {form.cover_enabled && (
              <div className="space-y-4 pt-2">
                <div className="flex justify-end">
                  <Button type="button" variant="outline" size="sm" data-testid="generate-cover-btn"
                    onClick={generateCover} disabled={coverLoading}>
                    {coverLoading
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Sparkles className="w-4 h-4 mr-2" />}
                    Genera copertina (AI)
                  </Button>
                </div>
                <div>
                  <Label>Oggetto / Titolo copertina</Label>
                  <Input data-testid="cover-title-input" value={form.cover_title}
                    onChange={(e) => update("cover_title", e.target.value)}
                    placeholder="Es. Ammodernamento postazione aziendale" className="mt-1.5" />
                </div>
                <div>
                  <Label>Sottotitolo</Label>
                  <Input data-testid="cover-subtitle-input" value={form.cover_subtitle}
                    onChange={(e) => update("cover_subtitle", e.target.value)}
                    placeholder="Es. Soluzioni IT su misura per la tua azienda" className="mt-1.5" />
                </div>
                <div>
                  <Label>Testo di presentazione</Label>
                  <Textarea data-testid="cover-text-input" value={form.cover_text}
                    onChange={(e) => update("cover_text", e.target.value)} rows={6}
                    placeholder="La lettera di presentazione apparirà qui (oppure premi 'Genera copertina (AI)')."
                    className="mt-1.5" />
                </div>
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-4">
            <h2 className="font-heading text-lg font-bold text-slate-900">Dettagli</h2>
            <div>
              <Label>Tipo di offerta</Label>
              <div className="flex items-center gap-2 mt-1.5">
                {[["acquisto", "Acquisto"], ["noleggio", "Noleggio"]].map(([val, lbl]) => (
                  <button key={val} type="button"
                    data-testid={`offer-type-${val}`}
                    onClick={() => update("offer_type", val)}
                    className={`text-sm font-semibold px-4 py-2 rounded-lg border transition-colors ${
                      (form.offer_type || "acquisto") === val
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}>
                    {lbl}
                  </button>
                ))}
              </div>
              {form.offer_type === "noleggio" && (
                <div className="mt-3 space-y-3" data-testid="noleggio-config">
                  <p className="text-xs text-slate-500" data-testid="offer-type-noleggio-hint">
                    Verrà inserita automaticamente nella copertina la presentazione «86BIT OfficeRelax» con vantaggi e condizioni del noleggio operativo.
                  </p>
                  <div>
                    <Label className="text-xs text-slate-500">Durata (mesi)</Label>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {[12, 24, 36, 48, 60].map((m) => (
                        <button key={m} type="button"
                          data-testid={`rental-months-${m}`}
                          onClick={() => update("rental_months", m)}
                          className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                            (Number(form.rental_months) || 0) === m
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}>
                          {m} mesi
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Canone mensile (€)</Label>
                    <Input type="number" data-testid="monthly-fee-input" value={form.monthly_fee || ""}
                      onChange={(e) => update("monthly_fee", e.target.value)}
                      placeholder="0 = calcolo automatico" className="mt-1.5" />
                    <p className="text-xs text-slate-400 mt-1">
                      {Number(form.monthly_fee) > 0
                        ? `Canone impostato manualmente: ${fmt(Number(form.monthly_fee), form.currency)} / mese`
                        : `Calcolo automatico: ${fmt(Number(form.rental_months) ? total / Number(form.rental_months) : 0, form.currency)} / mese (totale ÷ ${form.rental_months || 0} mesi)`}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label>Titolo preventivo</Label>
              <Input data-testid="quote-title-input" value={form.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder="Es. Offerta sito web aziendale" className="mt-1.5" />
            </div>
            <div>
              <Label>Cliente (da anagrafica 86BIT)</Label>
              <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" role="combobox" data-testid="customer-picker-btn"
                    className="w-full justify-between mt-1.5 font-normal">
                    <span className="flex items-center gap-2 truncate">
                      <Users className="w-4 h-4 text-slate-400" />
                      {form.client_name || "Cerca e seleziona un cliente…"}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Cerca cliente…" data-testid="customer-search-input" />
                    <CommandList>
                      <CommandEmpty>Nessun cliente trovato.</CommandEmpty>
                      <CommandGroup>
                        {customers.map((cust) => (
                          <CommandItem key={cust.id} value={cust.name}
                            data-testid={`customer-option-${cust.id}`}
                            onSelect={() => selectCustomer(cust)}>
                            <Check className={`mr-2 h-4 w-4 ${form.client_name === cust.name ? "opacity-100" : "opacity-0"}`} />
                            <span className="truncate">{cust.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-slate-400 mt-1">{customers.length} clienti disponibili. Oppure compila i campi manualmente.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nome cliente</Label>
                <Input data-testid="quote-client-name" value={form.client_name}
                  onChange={(e) => update("client_name", e.target.value)} placeholder="Azienda Srl" className="mt-1.5" />
              </div>
              <div>
                <Label>Email cliente</Label>
                <Input data-testid="quote-client-email" value={form.client_email}
                  onChange={(e) => update("client_email", e.target.value)} placeholder="info@azienda.it" className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Indirizzo cliente</Label>
              <Input data-testid="quote-client-address" value={form.client_address}
                onChange={(e) => update("client_address", e.target.value)} placeholder="Via Roma 1, Milano" className="mt-1.5" />
            </div>
            <div>
              <Label>Email per firma (DocuSign)</Label>
              <Input data-testid="quote-signing-email" type="email" value={form.signing_email}
                onChange={(e) => update("signing_email", e.target.value)}
                placeholder={form.client_email || "firma@azienda.it"} className="mt-1.5" />
              <p className="text-xs text-slate-400 mt-1">Indirizzo a cui verrà inviata la richiesta di firma DocuSign. Se vuoto, si usa l'email cliente.</p>
            </div>
            <div>
              <Label>Logo azienda destinataria (opzionale)</Label>
              <div className="flex items-center gap-3 mt-1.5">
                {form.client_logo ? (
                  <div className="relative">
                    <img src={form.client_logo} alt="Logo cliente" className="h-12 max-w-[140px] border border-slate-200 rounded p-1" data-testid="client-logo-preview" />
                    <button type="button" onClick={() => update("client_logo", "")}
                      className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-0.5 text-slate-500 hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="h-12 w-32 border-2 border-dashed border-slate-200 rounded flex items-center justify-center text-slate-300">
                    <ImageIcon className="w-5 h-5" />
                  </div>
                )}
                <input type="file" accept="image/*" id="client-logo-input" data-testid="client-logo-input"
                  className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) { toast.error("Logo troppo grande (max 2MB)"); return; }
                    update("client_logo", await fileToBase64(file));
                  }} />
                <Button type="button" variant="outline" size="sm"
                  onClick={() => document.getElementById("client-logo-input").click()} data-testid="client-logo-upload-btn">
                  <Upload className="w-4 h-4 mr-2" /> Carica logo cliente
                </Button>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setLogoDialogOpen(true)} data-testid="client-logo-library-btn">
                  <FolderPlus className="w-4 h-4 mr-2" /> Scegli da salvati{clientLogos.length ? ` (${clientLogos.length})` : ""}
                </Button>
                {form.client_logo && (
                  <Button type="button" variant="ghost" size="sm" disabled={savingLogo}
                    onClick={saveCurrentLogo} data-testid="client-logo-save-btn">
                    {savingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva logo
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-slate-900">Voci / Prodotti</h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCatalogOpen(true)} data-testid="add-from-catalog-button">
                  <Package className="w-4 h-4 mr-1" /> Aggiungi da catalogo
                </Button>
                <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-button">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi voce
                </Button>
              </div>
            </div>
            <div className="space-y-5">
              {form.items.map((item, idx) => (
                <div key={item.id} className="border border-slate-200 rounded-lg p-4" data-testid={`item-row-${idx}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400 pt-2">#{idx + 1}</span>
                    <div className="flex-1 grid md:grid-cols-12 gap-3">
                      <div className="md:col-span-6">
                        <Label className="text-xs">Titolo prodotto</Label>
                        <Input data-testid={`item-title-${idx}`} value={item.title}
                          onChange={(e) => updateItem(item.id, "title", e.target.value)}
                          placeholder="Es. Sviluppo homepage" className="mt-1" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs">Qtà</Label>
                        <Input type="number" min="0" data-testid={`item-qty-${idx}`} value={item.quantity}
                          onChange={(e) => updateItem(item.id, "quantity", e.target.value)} className="mt-1" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs">Prezzo unitario</Label>
                        <Input type="number" min="0" step="0.01" data-testid={`item-price-${idx}`} value={item.price}
                          onChange={(e) => updateItem(item.id, "price", e.target.value)}
                          className="mt-1 text-right" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs">Sconto %</Label>
                        <Input type="number" min="0" max="100" step="0.5" data-testid={`item-discount-${idx}`} value={item.discount ?? 0}
                          onChange={(e) => updateItem(item.id, "discount", e.target.value)}
                          placeholder="0" className="mt-1 text-right" />
                      </div>
                    </div>
                    {form.items.length > 1 && (
                      <button onClick={() => removeItem(item.id)} data-testid={`remove-item-${idx}`}
                        className="p-2 text-slate-400 hover:text-red-600 mt-5">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex justify-end -mt-1 mb-2">
                    <button type="button" onClick={() => saveItemToCatalog(item)} data-testid={`save-to-catalog-${idx}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-blue-600 transition-colors">
                      <FolderPlus className="w-3.5 h-3.5" /> Salva nel catalogo
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <Label className="text-xs text-slate-500">Condizione:</Label>
                    {[["nuovo", "Nuovo"], ["ricondizionato", "Ricondizionato"]].map(([val, lbl]) => (
                      <button key={val} type="button"
                        data-testid={`item-condition-${val}-${idx}`}
                        onClick={() => updateItem(item.id, "condition", val)}
                        className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                          (item.condition || "nuovo") === val
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <Label className="text-xs text-slate-500">Tipo:</Label>
                    {[["standard", "Standard"], ["server", "Server"]].map(([val, lbl]) => (
                      <button key={val} type="button"
                        data-testid={`item-type-${val}-${idx}`}
                        onClick={() => setProductType(item, val)}
                        className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                          (item.product_type || "standard") === val
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Label className="text-xs text-slate-500">Fornitura:</Label>
                    {[["acquisto", "Vendita"], ["noleggio", "Noleggio"]].map(([val, lbl]) => {
                      const acq = item.acquisition || form.offer_type || "acquisto";
                      return (
                        <button key={val} type="button"
                          data-testid={`item-acquisition-${val}-${idx}`}
                          onClick={() => updateItem(item.id, "acquisition", val)}
                          className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                            acq === val
                              ? "bg-emerald-600 text-white border-emerald-600"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}>
                          {lbl}
                        </button>
                      );
                    })}
                    {(item.acquisition || form.offer_type || "acquisto") === "noleggio" && (
                      <>
                        <span className="text-xs text-slate-400 ml-2">Mesi:</span>
                        {[12, 24, 36, 48, 60].map((m) => {
                          const cur = item.rental_months || form.rental_months || 36;
                          return (
                            <button key={m} type="button"
                              data-testid={`item-rental-months-${m}-${idx}`}
                              onClick={() => updateItem(item.id, "rental_months", m)}
                              className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                                cur === m
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                              }`}>
                              {m}
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                  <div className="mb-3">
                    <Label className="text-xs text-slate-500 flex items-center gap-1.5 mb-1.5"><Gift className="w-3.5 h-3.5" /> Prodotti in omaggio</Label>
                    <div className="flex flex-col gap-1.5">
                      {GIFT_OPTIONS.map((g) => {
                        const selected = (item.gifts || []).includes(g);
                        return (
                          <button key={g} type="button"
                            data-testid={`item-gift-${idx}-${GIFT_OPTIONS.indexOf(g)}`}
                            onClick={() => {
                              const cur = item.gifts || [];
                              updateItem(item.id, "gifts", selected ? cur.filter((x) => x !== g) : [...cur, g]);
                            }}
                            className={`flex items-center gap-2 text-left text-xs px-3 py-2 rounded-lg border transition-colors ${
                              selected ? "bg-teal-50 border-teal-300 text-teal-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                            }`}>
                            <span className={`w-4 h-4 rounded flex items-center justify-center border ${selected ? "bg-teal-600 border-teal-600" : "border-slate-300"}`}>
                              {selected && <Check className="w-3 h-3 text-white" />}
                            </span>
                            {g}
                          </button>
                        );
                      })}
                      {(item.gifts || []).filter((g) => !GIFT_OPTIONS.includes(g)).map((g) => (
                        <div key={g} className="flex items-center justify-between gap-2 text-xs px-3 py-2 rounded-lg border bg-teal-50 border-teal-300 text-teal-800">
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded bg-teal-600 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></span>
                            {g}
                          </span>
                          <button type="button" data-testid={`item-gift-remove-${idx}`}
                            onClick={() => updateItem(item.id, "gifts", (item.gifts || []).filter((x) => x !== g))}
                            className="text-teal-700 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Input data-testid={`item-gift-custom-input-${idx}`} value={customGift[item.id] || ""}
                        onChange={(e) => setCustomGift((m) => ({ ...m, [item.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomGift(item); } }}
                        placeholder="Scrivi un omaggio a mano…" className="h-9 text-xs" />
                      <Button type="button" variant="outline" size="sm" data-testid={`item-gift-add-${idx}`} onClick={() => addCustomGift(item)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs">Descrizione</Label>
                      <Button type="button" variant="ghost" size="sm" data-testid={`ai-generate-${idx}`}
                        onClick={() => generateAI(item)} disabled={aiLoading[item.id]}
                        className="h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                        {aiLoading[item.id]
                          ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                        Genera con AI
                      </Button>
                    </div>
                    <Textarea data-testid={`item-description-${idx}`} value={item.description}
                      onChange={(e) => updateItem(item.id, "description", e.target.value)}
                      placeholder="La descrizione generata dall'AI apparirà qui, oppure scrivila tu."
                      rows={3} />
                    <div className="mt-3 flex items-center gap-3">
                      {item.image ? (
                        <div className="relative">
                          <img src={item.image} alt="Prodotto" data-testid={`item-image-${idx}`}
                            className="w-20 h-20 object-cover rounded border border-slate-200" />
                          <button type="button" onClick={() => updateItem(item.id, "image", "")}
                            data-testid={`remove-image-${idx}`}
                            className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-0.5 text-slate-500 hover:text-red-600">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300">
                          <ImageIcon className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex flex-col gap-2">
                        <Button type="button" variant="outline" size="sm" data-testid={`ai-image-${idx}`}
                          onClick={() => generateImage(item)} disabled={imgLoading[item.id]}>
                          {imgLoading[item.id]
                            ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                          Genera con AI
                        </Button>
                        <input type="file" accept="image/*" id={`upload-image-${idx}`} data-testid={`upload-image-input-${idx}`}
                          className="hidden" onChange={(e) => uploadImage(item, e.target.files?.[0])} />
                        <Button type="button" variant="ghost" size="sm" data-testid={`upload-image-${idx}`}
                          onClick={() => document.getElementById(`upload-image-${idx}`).click()} disabled={imgLoading[item.id]}>
                          <Upload className="w-3.5 h-3.5 mr-1" /> Carica dal PC
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <Label className="text-xs text-slate-500">Altre immagini del prodotto</Label>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {(item.gallery || []).map((g, gi) => (
                          <div key={gi} className="relative">
                            <img src={g} alt="" data-testid={`item-gallery-img-${idx}-${gi}`}
                              className="w-14 h-14 object-cover rounded border border-slate-200" />
                            <button type="button"
                              onClick={() => updateItem(item.id, "gallery", (item.gallery || []).filter((_, i) => i !== gi))}
                              className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-0.5 text-slate-500 hover:text-red-600">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <input type="file" accept="image/*" multiple id={`upload-gallery-${idx}`} data-testid={`upload-gallery-input-${idx}`}
                          className="hidden" onChange={(e) => uploadGallery(item, e.target.files)} />
                        <button type="button" data-testid={`add-gallery-${idx}`}
                          onClick={() => document.getElementById(`upload-gallery-${idx}`).click()} disabled={imgLoading[item.id]}
                          className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-dashed border-slate-300 hover:bg-slate-50 text-slate-600">
                          <Plus className="w-3.5 h-3.5" /> Aggiungi foto
                        </button>
                      </div>
                      <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-500">Varianti AI:</span>
                        {[1, 2, 3, 4].map((n) => (
                          <button key={n} type="button" onClick={() => setVarCount((s) => ({ ...s, [item.id]: n }))}
                            data-testid={`item-varcount-${n}-${idx}`}
                            className={`text-xs font-semibold w-6 h-6 rounded-full border ${(varCount[item.id] || 3) === n ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{n}</button>
                        ))}
                        <Button type="button" variant="outline" size="sm" data-testid={`gen-variations-${idx}`}
                          onClick={() => generateVariations(item)}
                          disabled={varLoading[item.id] || (!item.image && !(item.gallery || []).length)}>
                          {varLoading[item.id] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                          Genera {varCount[item.id] || 3} varianti dalle foto
                        </Button>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1.5">L'AI crea nuove inquadrature/angoli dello stesso prodotto a partire dalle foto reali caricate.</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Label className="text-xs text-slate-500">Garanzia:</Label>
                    {[0, 12, 24, 36, 48, 60].map((m) => (
                      <button key={m} type="button"
                        data-testid={`item-warranty-${m}-${idx}`}
                        onClick={() => updateItem(item.id, "warranty_months", m)}
                        className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                          (item.warranty_months || 0) === m
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}>
                        {m === 0 ? "Nessuna" : `${m} mesi`}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Caratteristiche tecniche (opzionale)
                    </Label>
                    {(item.product_type || "standard") === "server" ? (
                      <div className="mt-2 space-y-2" data-testid={`item-server-specs-${idx}`}>
                        {(item.spec_rows || []).map((row, ri) => (
                          <div key={ri} className="flex gap-2 items-start">
                            <Input data-testid={`item-specrow-label-${idx}-${ri}`} value={row.label || ""}
                              onChange={(e) => updateSpecRow(item.id, ri, "label", e.target.value)}
                              placeholder="Etichetta (es. Processor - CPU)" className="h-9 text-xs flex-[0_0_38%]" />
                            <Input data-testid={`item-specrow-value-${idx}-${ri}`} value={row.value || ""}
                              onChange={(e) => updateSpecRow(item.id, ri, "value", e.target.value)}
                              placeholder="Valore (es. 2x Intel Xeon Gold 6138…)" className="h-9 text-xs flex-1" />
                            <button type="button" data-testid={`item-specrow-remove-${idx}-${ri}`}
                              onClick={() => removeSpecRow(item.id, ri)}
                              className="mt-1 text-slate-400 hover:text-red-600 shrink-0"><X className="w-4 h-4" /></button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" data-testid={`item-specrow-add-${idx}`}
                          onClick={() => addSpecRow(item.id)} className="mt-1">
                          <Plus className="w-4 h-4 mr-1.5" /> Aggiungi riga
                        </Button>
                      </div>
                    ) : (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                        {SPEC_FIELDS.map(([k, lbl]) => (
                          <div key={k}>
                            <Label className="text-[11px] text-slate-400">{lbl}</Label>
                            <Input data-testid={`item-spec-${k}-${idx}`} value={(item.specs || {})[k] || ""}
                              onChange={(e) => updateSpec(item.id, k, e.target.value)}
                              placeholder={lbl} className="mt-1 h-9" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-900">Pagina condizioni e firma</h2>
                <p className="text-sm text-slate-500">Pagina finale con condizioni commerciali e spazio per la firma.</p>
              </div>
              <Switch checked={form.closing_enabled} onCheckedChange={(v) => update("closing_enabled", v)}
                data-testid="closing-enabled-switch" />
            </div>
            {form.closing_enabled && (
              <div className="space-y-4 pt-2">
                <div>
                  <Label>Titolo pagina condizioni</Label>
                  <Input data-testid="closing-title-input" value={form.closing_title}
                    onChange={(e) => update("closing_title", e.target.value)}
                    placeholder="Es. Condizioni Semplificate di Fornitura" className="mt-1.5" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Offerta valida fino al</Label>
                    <Input type="date" data-testid="valid-until-input" value={form.valid_until}
                      onChange={(e) => update("valid_until", e.target.value)} className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Pagamento</Label>
                    <Input data-testid="payment-terms-input" value={form.payment_terms}
                      onChange={(e) => update("payment_terms", e.target.value)}
                      placeholder="Es. Riba 30 gg" className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Nota IVA</Label>
                    <Input data-testid="vat-note-input" value={form.vat_note}
                      onChange={(e) => update("vat_note", e.target.value)}
                      placeholder="IVA esclusa" className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Luogo (per la firma)</Label>
                    <Input data-testid="signing-place-input" value={form.signing_place}
                      onChange={(e) => update("signing_place", e.target.value)}
                      placeholder="Es. Scanzorosciate" className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label>Testo di chiusura</Label>
                  <Textarea data-testid="closing-text-input" value={form.closing_text}
                    onChange={(e) => update("closing_text", e.target.value)} rows={3}
                    placeholder="Testo di chiusura (compilato anche da 'Genera copertina (AI)')."
                    className="mt-1.5" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: summary */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-4 sticky top-6">
            <h2 className="font-heading text-lg font-bold text-slate-900">Riepilogo</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Valuta</Label>
                <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
                  <SelectTrigger data-testid="currency-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR €</SelectItem>
                    <SelectItem value="USD">USD $</SelectItem>
                    <SelectItem value="GBP">GBP £</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">IVA %</Label>
                <Input type="number" data-testid="tax-rate-input" value={form.tax_rate}
                  onChange={(e) => update("tax_rate", e.target.value)} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Stato</Label>
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger data-testid="status-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Bozza</SelectItem>
                  <SelectItem value="sent">Inviato</SelectItem>
                  <SelectItem value="signed">Firmato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t border-slate-200 pt-4 space-y-2">
              {nolItems.length > 0 && (
                <div className="flex justify-between font-heading text-lg font-black text-blue-600 pb-1" data-testid="summary-canone-block">
                  <span>Canone / mese</span><span data-testid="summary-canone">{fmt(nolFee, form.currency)}</span>
                </div>
              )}
              {acqItems.length > 0 && (
                <div className="flex justify-between font-heading text-lg font-black text-slate-900" data-testid="summary-acquisto-block">
                  <span>Totale acquisto</span><span data-testid="summary-acquisto">{fmt(acqTotal, form.currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs text-slate-400 pt-1 border-t border-slate-100">
                <span>Valore totale attrezzatura</span><span data-testid="summary-subtotal">{fmt(subtotal, form.currency)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="catalog-picker-dialog">
          <DialogHeader>
            <DialogTitle>Aggiungi da catalogo</DialogTitle>
            <DialogDescription>Seleziona un prodotto precompilato per aggiungerlo all'offerta.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="catalog-picker-search" value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)}
              placeholder="Cerca un prodotto…" className="pl-9" />
          </div>
          <div className="overflow-y-auto mt-3 space-y-2 pr-1">
            {catalog.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">
                Nessun prodotto nel catalogo. Creane uno nella pagina <b>Catalogo Prodotti</b>.
              </p>
            ) : (
              catalog
                .filter((p) => !catalogSearch.trim() || (p.title || "").toLowerCase().includes(catalogSearch.toLowerCase()))
                .map((p) => (
                  <button key={p.id} type="button" onClick={() => addFromCatalog(p)}
                    data-testid={`catalog-pick-${p.id}`}
                    className="w-full flex items-center gap-3 text-left p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 transition-colors">
                    <div className="w-12 h-12 rounded-md bg-slate-50 flex items-center justify-center shrink-0 overflow-hidden">
                      {p.image ? <img src={p.image} alt="" className="w-full h-full object-contain" /> : <Package className="w-5 h-5 text-slate-300" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{p.title}</p>
                      <p className="text-xs text-slate-500">
                        € {Number(p.price).toFixed(2)}
                        {p.condition === "ricondizionato" && " · Ricondizionato"}
                        {(p.gifts || []).length > 0 && ` · ${p.gifts.length} omaggio`}
                      </p>
                    </div>
                    <Plus className="w-4 h-4 text-blue-600 shrink-0" />
                  </button>
                ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={logoDialogOpen} onOpenChange={setLogoDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="client-logo-library-dialog">
          <DialogHeader>
            <DialogTitle>Loghi cliente salvati</DialogTitle>
            <DialogDescription>
              Scegli un logo salvato in precedenza. I loghi associati a un cliente si agganciano
              automaticamente quando selezioni quel cliente dall'anagrafica.
            </DialogDescription>
          </DialogHeader>
          {clientLogos.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="client-logo-library-empty">
              Nessun logo salvato. Carica un logo cliente e premi "Salva logo".
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto py-1">
              {clientLogos.map((logo) => (
                <div key={logo.id} className="relative border border-slate-200 rounded-lg p-3 hover:border-blue-400 transition-colors group"
                  data-testid={`client-logo-item-${logo.id}`}>
                  <button type="button" onClick={() => applyLogo(logo)}
                    className="block w-full" data-testid={`client-logo-apply-${logo.id}`}>
                    <div className="h-16 flex items-center justify-center bg-slate-50 rounded mb-2 overflow-hidden">
                      <img src={logo.image} alt={logo.label} className="max-h-14 max-w-full object-contain" />
                    </div>
                    <div className="text-xs font-medium text-slate-700 truncate">{logo.label}</div>
                    {logo.client_name && <div className="text-[11px] text-slate-400 truncate">{logo.client_name}</div>}
                  </button>
                  <button type="button" onClick={() => deleteLogo(logo.id)}
                    className="absolute top-1.5 right-1.5 bg-white border border-slate-200 rounded-full p-1 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`client-logo-delete-${logo.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
